/* eslint-disable react-hooks/exhaustive-deps */
import { paramCase } from 'change-case';
import { Helmet } from 'react-helmet-async';
import { useState, useEffect, useRef } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useDebounce } from 'use-lodash-debounce';
// @mui
import {
    Box,
    Card,
    Table,
    Stack,
    Switch,
    Button,
    Chip,
    TableBody,
    Container,
    TableContainer,
    TablePagination,
    FormControlLabel,
    Divider,
    Typography,
    alpha,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useSnackbar from 'src/hooks/useCustomSnackbar';
// components
import { useLocation } from 'react-router';
import { updateSelectedPracticeForMenu } from 'src/redux/slices/mergeTags';
import { useSettingsContext } from 'src/components/settings/context';
import useIsSuperAdmin from 'src/hooks/useIsSuperAdmin';
import { Scrollbar, Iconify } from '../../../components';
import {
    TableEmptyRows,
    TableHeadCustom,
    TableNoData,
} from '../../../components/table';
import { TableLoading } from '../../../components/loading';
// constant
import {
    STATUS_OPTIONS,
    validInviteUserLabels,
    inValidInviteUserLabels,
    ADMIN_STATUS_OPTIONS
} from '../../../constants/PracticeUserListConst';
// hooks
import {
    useTabs,
    useAuth,
    useTable
} from '../../../hooks';
import { getComparator, stableSort } from '../../../hooks/useTable';
// routes
import { PATH_DASHBOARD } from '../../../routes/paths';
// utils
import capitalizeFirstLetter from '../../../utils/capitalizedFunc';
// sections
import { UserTableListRow, UserTableListToolbar } from '../../../sections/@dashboard/user/list';
// store
import { clearMessage, getInviteUser, DeleteInviteUser, disableUser, enableUser, getPracticeUserListWithFilter, startLoading } from '../../../redux/slices/practiceUser';
import { dispatch, useSelector } from '../../../redux/store';
// style
import { formControlLabelStyle } from '../../../styles/PracticeUserListStyle';
import { settingsTableCardStyle } from '../../../styles/SettingsMenuStyle';

// ── Role tab metadata (icon + colour per role) ──────────────────────
const ROLE_TAB_META = {
    all:          { icon: 'solar:users-group-rounded-bold-duotone', color: '#64748b' },
    ADMIN:        { icon: 'solar:shield-user-bold-duotone',         color: '#B72136' },
    USER:         { icon: 'solar:user-bold-duotone',                color: '#0C53B7' },
    Practitioner: { icon: 'solar:stethoscope-bold-duotone',         color: '#0A5554' },
    TCO:          { icon: 'solar:chat-round-money-bold-duotone',    color: '#B76E00' },
    Disabled:     { icon: 'solar:user-block-bold-duotone',          color: '#919EAB' },
    'INVITE USER':{ icon: 'solar:letter-bold-duotone',              color: '#00A76F' },
};

// ----------------------------------------------------------------------

export default function PracticeUserList() {
    const {
        dense,
        page,
        order,
        orderBy,
        rowsPerPage,
        setPage,
        selected,
        setSelected,
        onSelectAllRows,
        onChangeDense,
        onChangePage,
        onChangeRowsPerPage,
    } = useTable();

    const theme = useTheme();
    const settings = useSettingsContext();
    const { isSuperAdminUser } = useIsSuperAdmin();
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const { user } = useAuth();
    const [adminUser, setAdminUser] = useState('');
    const [filterName, setFilterName] = useState('');
    const [filterRole] = useState('all');
    const { currentTab: filterStatus, onChangeTab: onChangeFilterStatus } = useTabs('all');
    const { message, error, practiceUserWithFilter: { results, count }, inviteUser, isLoading, practiceUser } = useSelector((state) => state.practiceUser);
    const [tableData, setTableData] = useState([]);
    const { practiceId } = useSelector((state) => state.practiceTreatmentList);
    const isEdit = adminUser;
    const denseHeight = dense ? 52 : 72;
    const [tabFilterData, setTabFilterData] = useState('');
    const debouncedValue = useDebounce(filterName, 800);

    const validInviteUser = () => filterStatus === 'INVITE USER';

    const filterData = (data) => data.filter(el => el.user && !el.user.is_superuser);

    const tableHeadHandler = () => validInviteUser() ? validInviteUserLabels : inValidInviteUserLabels;

    const TABLE_HEAD = tableHeadHandler();

    const getUserList = () => {
        if (validInviteUser()) {
            return inviteUser.length ? inviteUser.filter(el => !el.is_accepted) : [];
        }
        return results && results.length ? filterData(results) : [];
    };

    const getRoleFilter = () => {
        if (isSuperAdminUser()) {
            return tabFilterData === 'all' || tabFilterData === 'INVITE USER' || tabFilterData === 'Disabled' ? '' : tabFilterData;
        }
        return tabFilterData === '' || tabFilterData === 'all' || tabFilterData === 'INVITE USER' || tabFilterData === 'Disabled' ? 'ADMIN,USER,Practitioner,TCO' : tabFilterData;
    };
    const isEnabled = () => tabFilterData === 'Disabled' ? true : '';

    useEffect(() => {
        if (tabFilterData) {
            const data = {
                practiceId,
                limit: 10,
                offset: 0,
                role: getRoleFilter(),
                is_enabled: isEnabled(),
                searchQuery: filterName
            };
            dispatch(getPracticeUserListWithFilter(data));
            resetPagination();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tabFilterData]);

    const isFirstRender = useRef(true);

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        if (filterName !== null) {
            setPage(0);
            const data = {
                practiceId,
                limit: 10,
                offset: 0,
                role: getRoleFilter(),
                is_enabled: isEnabled(),
                searchQuery: debouncedValue
            };
            dispatch(getPracticeUserListWithFilter(data));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedValue]);

    const handleChangePerPage = (event) => {
        onChangeRowsPerPage(event);
        setPage(0);
        const data = {
            practiceId,
            limit: event.target.value,
            offset: 0,
            role: getRoleFilter(),
            searchQuery: filterName,
            is_enabled: isEnabled(),
        };
        dispatch(getPracticeUserListWithFilter(data));
    };

    const handleChangePage = (event, newPage) => {
        onChangePage(event, newPage);
        const data = {
            practiceId,
            limit: rowsPerPage,
            offset: rowsPerPage * newPage,
            role: getRoleFilter(),
            searchQuery: filterName,
            is_enabled: isEnabled(),
        };
        dispatch(getPracticeUserListWithFilter(data));
    };

    useEffect(() => {
        if (adminUser?.role === 'ADMIN' && !STATUS_OPTIONS.includes('INVITE USER')) {
            STATUS_OPTIONS.push('INVITE USER');
        }
    }, [adminUser?.role]);

    useEffect(() => {
        if (practiceUser && practiceUser?.results?.length) {
            const currentUser = user?.email;
            setTableData(getUserList());
            setAdminUser(practiceUser?.results?.find((users) => users?.user?.email === currentUser));
        }
    }, [practiceUser, inviteUser]);

    useEffect(() => {
        if (practiceId) {
            dispatch(getInviteUser(practiceId));
            const data = {
                practiceId,
                limit: 10,
                offset: 0,
                role: getRoleFilter(),
            };
            dispatch(getPracticeUserListWithFilter(data));
            resetPagination();
        }
    }, [practiceId]);

    useEffect(() => {
        if (message) {
            enqueueSnackbar(message, { variant: 'success', anchorOrigin: { vertical: 'top', horizontal: 'center' } });
            setTimeout(() => {
                dispatch(clearMessage());
            }, 3000);
        }
        if (error) {
            enqueueSnackbar(error, { variant: 'error', anchorOrigin: { vertical: 'top', horizontal: 'center' } });
            setTimeout(() => {
                dispatch(clearMessage());
            }, 3000);
        }
    }, [message, error]);

    const handleFilterName = (filterName) => {
        setFilterName(filterName);
        setPage(0);
    };

    const handleDeleteRow = (id) => {
        if (!validInviteUser()) {
            const deleteRow = tableData.filter((row) => row.id !== id);
            setSelected([]);
            setTableData(deleteRow);
            return;
        }
        dispatch(DeleteInviteUser(practiceId, id, handleSuccess));
    };

    const handleSwitchChange = (row) => {
        if (row.is_enabled) {
            const data = {
                practice: row.practice,
                id: row.id,
                user: row.user,
                role: row.role,
                notification_preferences: row.notification_preferences
            };
            dispatch(disableUser(practiceId, data, handleUpdate));
            return;
        }
        const data = {
            practice: row.practice,
            id: row.id,
            user: row.user,
            role: row.role,
            notification_preferences: row.notification_preferences
        };
        dispatch(enableUser(practiceId, data, handleUpdate));
    };

    const handleUpdate = (data) => {
        enqueueSnackbar(typeof data.message === 'string' ? data.message : 'Something went wrong', {
            variant: data.success ? 'success' : 'error',
            anchorOrigin: {
                vertical: 'top',
                horizontal: 'center'
            },
            id: 'success-bar'
        });
        const getData = {
            practiceId,
            limit: rowsPerPage,
            offset: rowsPerPage * page,
            role: getRoleFilter(),
            is_enabled: isEnabled(),
            searchQuery: filterName
        };
        dispatch(getPracticeUserListWithFilter(getData));
    };

    const handleSuccess = (data) => {
        enqueueSnackbar(typeof data.message === 'string' ? data.message : 'Something went wrong', {
            variant: data.success ? 'success' : 'error',
            anchorOrigin: { vertical: 'top', horizontal: 'center' }
        });
        if (data.success) {
            resetPagination();
        }
    };

    const dataFiltered = applySortFilter({
        tableData: getUserList(),
        comparator: getComparator(order, 'role'),
        filterRole,
        filterStatus,
    });

    const handleBulkEnableDisable = (enable) => {
        const userList = getUserList();
        selected.forEach((id) => {
            const row = userList.find((r) => r.id === id);
            if (row) {
                const data = {
                    practice: row.practice,
                    id: row.id,
                    user: row.user,
                    role: row.role,
                    notification_preferences: row.notification_preferences
                };
                if (enable) {
                    dispatch(enableUser(practiceId, data, handleUpdate));
                } else {
                    dispatch(disableUser(practiceId, data, handleUpdate));
                }
            }
        });
        setSelected([]);
    };

    const handleBulkDeleteInvites = (selectedIds) => {
        selectedIds.forEach((id) => {
            dispatch(DeleteInviteUser(practiceId, id, handleSuccess));
        });
        setSelected([]);
    };

    const isNotFound =
        (!count && !!filterName) ||
        (!count && !!filterRole) ||
        (!count && !!filterStatus);

    const isNotFoundInviteUser =
        (!dataFiltered.length && !!filterName) ||
        (!dataFiltered.length && !!filterRole) ||
        (!dataFiltered.length && !!filterStatus);

    const handleChangeTab = (event, newValue) => {
        dispatch(startLoading());
        setTabFilterData(newValue);
        onChangeFilterStatus(event, newValue);
    };

    const resetPagination = () => {
        onChangeRowsPerPage({ target: { value: 10 } });
        onChangePage('', 0);
    };

    const location = useLocation()

    useEffect(() => {
        dispatch(updateSelectedPracticeForMenu("practice_users"))
    }, [location.pathname])

    const activeTabOptions = adminUser?.role === 'ADMIN' ? ADMIN_STATUS_OPTIONS : STATUS_OPTIONS;

    return (
        <>
            <Helmet>
                <title> Practice Users : List | Dental SEM CRM </title>
            </Helmet>
            <Container maxWidth={settings.themeStretch ? false : 'xl'}>
                {/* ── Header ──────────────────────────────────────────── */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box>
                        <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
                            Team Members
                        </Typography>
                        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mt: 0.5, mb: 2 }}>
                            Manage users, roles, and permissions for your practice.
                        </Typography>
                    </Box>
                    {isEdit &&
                        <Button
                            variant="contained"
                            component={RouterLink}
                            to={PATH_DASHBOARD.user.new}
                            id='invite-btn'
                            startIcon={<Iconify icon={'eva:plus-fill'} />}
                            sx={{
                                bgcolor: '#00A76F',
                                '&:hover': { bgcolor: '#007B55' },
                                textTransform: 'none',
                                fontWeight: 600,
                                borderRadius: '10px',
                                px: 2.5,
                                mt: 0.5,
                            }}
                        >
                            Invite User
                        </Button>
                    }
                </Box>
                <Divider sx={{ mb: 3 }} />

                {/* ── iOS-style Segmented Control (Role Tabs) ─────────── */}
                <Box sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '6px',
                    bgcolor: '#f1f5f9',
                    borderRadius: '14px',
                    p: '5px',
                    mb: 3,
                }}>
                    {activeTabOptions.map((tab) => {
                        const meta = ROLE_TAB_META[tab] || ROLE_TAB_META.all;
                        const isActive = filterStatus === tab;
                        const label = tab === 'TCO' ? tab : capitalizeFirstLetter(tab.toLowerCase());
                        return (
                            <Button
                                key={tab}
                                disableRipple
                                onClick={(e) => handleChangeTab(e, tab)}
                                startIcon={
                                    <Box sx={{
                                        width: 22, height: 22, borderRadius: '7px',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        bgcolor: isActive ? alpha(meta.color, 0.12) : 'transparent',
                                        transition: 'all 0.2s',
                                    }}>
                                        <Iconify
                                            icon={meta.icon}
                                            width={14}
                                            sx={{ color: isActive ? meta.color : '#94a3b8' }}
                                        />
                                    </Box>
                                }
                                sx={{
                                    borderRadius: '10px',
                                    px: 2,
                                    py: 0.75,
                                    fontSize: '13px',
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    minWidth: 0,
                                    bgcolor: isActive ? '#fff' : 'transparent',
                                    color: isActive ? '#1e293b' : '#94a3b8',
                                    boxShadow: isActive ? '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.06)' : 'none',
                                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                    '&:hover': {
                                        bgcolor: isActive ? '#fff' : alpha('#000', 0.04),
                                    },
                                }}
                            >
                                {label}
                            </Button>
                        );
                    })}
                </Box>

                <Card sx={settingsTableCardStyle}>
                            {!validInviteUser() && <UserTableListToolbar
                                filterName={filterName}
                                onFilterName={handleFilterName}
                                onResetFilter={() => setFilterName('')}
                            />}
                            {/* ── Bulk Action Bar ──────────────────────────── */}
                            {selected.length > 0 && (
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    px: 2.5,
                                    py: 1.5,
                                    bgcolor: alpha('#3b82f6', 0.06),
                                    borderBottom: `1px solid ${alpha('#3b82f6', 0.12)}`,
                                }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                        <Chip
                                            label={`${selected.length} selected`}
                                            size="small"
                                            sx={{
                                                fontWeight: 700,
                                                fontSize: '12px',
                                                bgcolor: alpha('#3b82f6', 0.12),
                                                color: '#3b82f6',
                                                height: 26,
                                            }}
                                        />
                                        <Typography sx={{ fontSize: '13px', color: '#64748b' }}>
                                            of {tableData.length} members
                                        </Typography>
                                    </Box>
                                    <Stack direction="row" spacing={1}>
                                        {!validInviteUser() && (
                                            <>
                                                <Button
                                                    size="small"
                                                    variant="contained"
                                                    startIcon={<Iconify icon="eva:checkmark-circle-2-fill" width={16} />}
                                                    onClick={() => handleBulkEnableDisable(true)}
                                                    sx={{
                                                        bgcolor: '#22c55e',
                                                        '&:hover': { bgcolor: '#16a34a' },
                                                        textTransform: 'none',
                                                        fontWeight: 600,
                                                        fontSize: '12px',
                                                        borderRadius: '8px',
                                                        px: 1.5,
                                                        py: 0.5,
                                                        boxShadow: 'none',
                                                    }}
                                                >
                                                    Enable
                                                </Button>
                                                <Button
                                                    size="small"
                                                    variant="contained"
                                                    startIcon={<Iconify icon="eva:slash-fill" width={16} />}
                                                    onClick={() => handleBulkEnableDisable(false)}
                                                    sx={{
                                                        bgcolor: '#f59e0b',
                                                        '&:hover': { bgcolor: '#d97706' },
                                                        textTransform: 'none',
                                                        fontWeight: 600,
                                                        fontSize: '12px',
                                                        borderRadius: '8px',
                                                        px: 1.5,
                                                        py: 0.5,
                                                        boxShadow: 'none',
                                                    }}
                                                >
                                                    Disable
                                                </Button>
                                            </>
                                        )}
                                        {validInviteUser() && (
                                            <Button
                                                size="small"
                                                variant="contained"
                                                startIcon={<Iconify icon="solar:trash-bin-trash-bold" width={16} />}
                                                onClick={() => handleBulkDeleteInvites(selected)}
                                                sx={{
                                                    bgcolor: '#ef4444',
                                                    '&:hover': { bgcolor: '#dc2626' },
                                                    textTransform: 'none',
                                                    fontWeight: 600,
                                                    fontSize: '12px',
                                                    borderRadius: '8px',
                                                    px: 1.5,
                                                    py: 0.5,
                                                    boxShadow: 'none',
                                                }}
                                            >
                                                Delete
                                            </Button>
                                        )}
                                        <Button
                                            size="small"
                                            variant="text"
                                            onClick={() => setSelected([])}
                                            sx={{
                                                textTransform: 'none',
                                                fontWeight: 600,
                                                fontSize: '12px',
                                                color: '#94a3b8',
                                                '&:hover': { bgcolor: alpha('#000', 0.04) },
                                            }}
                                        >
                                            Clear
                                        </Button>
                                    </Stack>
                                </Box>
                            )}

                            <Scrollbar>
                                <TableContainer sx={{ minWidth: 800, position: 'relative' }}>
                                    <Table size={dense ? 'small' : 'medium'} sx={{
                                        '.MuiTableCell-root': {
                                            boxShadow: 'none !important',
                                            borderColor: theme.palette.divider,
                                        },
                                        '.MuiTableHead-root .MuiTableCell-root': {
                                            bgcolor: theme.palette.background.neutral,
                                            color: theme.palette.text.secondary,
                                            fontWeight: 600,
                                            fontSize: '0.8125rem',
                                            py: dense ? 1 : 1.5,
                                            borderBottom: `1px solid ${theme.palette.divider}`,
                                        },
                                        '.MuiTableBody-root .MuiTableRow-root:hover': {
                                            bgcolor: 'action.hover',
                                        },
                                    }}>
                                        <TableHeadCustom
                                            order={order}
                                            orderBy={orderBy}
                                            headLabel={TABLE_HEAD}
                                            rowCount={tableData.length}
                                            numSelected={selected.length}
                                            onSelectAllRows={(checked) =>
                                                onSelectAllRows(
                                                    checked,
                                                    tableData.map((row) => row.id)
                                                )
                                            }
                                        />
                                        <TableBody>
                                            <TableLoading tableData={isLoading && 'No Found'} pageCount={isLoading && 'No Found'} />
                                            {(validInviteUser() ? (stableSort(dataFiltered, getComparator(order, 'role')).slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)) : results)?.map((row, index) => (
                                                <UserTableListRow
                                                    key={index}
                                                    row={row}
                                                    selected={selected.includes(row.id)}
                                                    onSelectRow={() => {
                                                        const checked = selected.includes(row.id);
                                                        if (checked) {
                                                            setSelected(selected.filter((s) => s !== row.id));
                                                        } else {
                                                            setSelected([...selected, row.id]);
                                                        }
                                                    }}
                                                    onDeleteRow={() => handleDeleteRow(row.id)}
                                                    onEditRow={() => navigate(PATH_DASHBOARD.user.edit(paramCase(JSON.stringify(row?.id))))}
                                                    tableListData={getUserList()}
                                                    inviteUser={validInviteUser()}
                                                    isEdit={isEdit}
                                                    handleSwitchChange={handleSwitchChange}
                                                    adminUser={adminUser?.role}
                                                    currentUser={user?.email}
                                                />
                                            ))}
                                            <TableEmptyRows height={denseHeight} />
                                            <TableNoData isNotFound={validInviteUser() ? isNotFoundInviteUser : isNotFound} />
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Scrollbar>

                            <Box sx={{ position: 'relative', borderTop: `1px solid ${theme.palette.divider}`, px: 1 }}>
                                {validInviteUser() ?
                                    dataFiltered.length > 10 && <TablePagination
                                        rowsPerPageOptions={[10, 25]}
                                        component="div"
                                        count={dataFiltered.length}
                                        rowsPerPage={rowsPerPage}
                                        page={page}
                                        onPageChange={onChangePage}
                                        onRowsPerPageChange={onChangeRowsPerPage}
                                    />
                                    :
                                    count > 10 && <TablePagination
                                        rowsPerPageOptions={[10, 25]}
                                        component="div"
                                        count={count}
                                        rowsPerPage={rowsPerPage}
                                        page={page}
                                        onPageChange={(event, newPage) => handleChangePage(event, newPage)}
                                        onRowsPerPageChange={(event) => handleChangePerPage(event)}
                                    />
                                }
                                <FormControlLabel
                                    control={<Switch checked={dense} onChange={onChangeDense} />}
                                    label="Dense"
                                    sx={formControlLabelStyle}
                                />
                            </Box>
                </Card>
            </Container>
        </>
    );
}

// ----------------------------------------------------------------------

function applySortFilter({ tableData, comparator, filterStatus, filterRole }) {
    const stabilizedThis = tableData.map((el, index) => [el, index]);

    stabilizedThis.sort((a, b) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) return order;
        return a[1] - b[1];
    });
    tableData = stabilizedThis.map((el) => el[0]);

    const usersData = [];

    // ----- To combine the first name, last name and salutation of a user
    tableData.forEach((user) => {
        usersData.push({
            ...user,
            name: `${user.user && user.user.salutation} ${user.user && user.user.first_name} ${user.user && user.user.last_name}`,
        });
    });

    if (filterStatus !== 'all') {
        if (filterStatus !== 'INVITE USER') {
            tableData = tableData.filter((item) => item.role === filterStatus);
        }
    }

    if (filterRole !== 'all') {
        tableData = tableData.filter((item) => item.role === filterRole);
    }

    return tableData;
}
